import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PartyPopper, X, Plus, Calendar, Users, MapPin, Phone, KeyRound, CheckCircle2 } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'
import CsvControls from '../components/CsvControls'

interface EventTier {
  id: string
  name_en: string
  name_ar: string
  description_en: string | null
  description_ar: string | null
  price_fils: number
  sort_order: number
  active: number
}

interface Event {
  id: string
  name_en: string
  name_ar: string
  description_en: string | null
  description_ar: string | null
  type: string
  starts_at: string
  ends_at: string | null
  location: string
  capacity: number
  tickets_sold: number
  ticket_price_fils: number
  max_per_user: number | null
  image_key: string | null
  active: number
  allow_cash: number
  allow_card_on_arrival: number
  allow_cliq: number
  allow_card_online: number
  notes_field_enabled: number
  notes_field_placeholder_en: string | null
  notes_field_placeholder_ar: string | null
  time_mode: 'none' | 'display' | 'pick'
  daily_open_time: string | null
  daily_close_time: string | null
  show_seats_left: number
  refundable: number
  cancel_window_hours: number | null
  venue_id: string | null
  closed_weekdays: string | null
  tiers?: EventTier[]
}

interface Ticket {
  id: string
  user_id: string
  user_name: string
  user_phone: string
  quantity: number
  total_paid_fils: number
  payment_method: string
  status: 'confirmed' | 'cancelled' | 'used'
  redemption_code: string
  redeemed_at: string | null
  created_at: string
  attendance_date: string | null
  attendance_time: string | null
  cancelled_at: string | null
  cs_transaction_id: string | null
  card_brand: string | null
  card_last4: string | null
  refund_transaction_id: string | null
  refunded_at: string | null
}

export default function Events() {
  const [filter, setFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming')
  const [editing, setEditing] = useState<Event | null | 'new'>(null)
  const [viewing, setViewing] = useState<Event | null>(null)
  const [redeemOpen, setRedeemOpen] = useState(false)

  const { data, isLoading } = useQuery<{ events: Event[] }>({
    queryKey: ['admin-events', filter],
    queryFn: () => api.get(`/events?status=${filter}`).then(r => r.data),
  })

  const events = data?.events ?? []

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <PartyPopper size={24} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold text-white">Events & Tastings</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setRedeemOpen(true)}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 flex items-center gap-2"
          >
            <KeyRound size={14} /> Door Check-in
          </button>
          <button
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] flex items-center gap-2"
          >
            <Plus size={14} /> New Event
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {(['upcoming', 'past', 'all'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border transition ${
              filter === s
                ? 'bg-[#C41E3A] text-white border-[#C41E3A]'
                : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
      {!isLoading && events.length === 0 && (
        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-8 text-center">
          <PartyPopper size={32} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400 text-sm">No events in this view.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {events.map(e => (
          <div key={e.id} className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg overflow-hidden">
            {e.image_key && (
              <img src={`https://files.drinx.jo/${e.image_key}`} alt=""
                className="w-full aspect-[16/9] object-cover bg-[#0a0a0a]" />
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <h3 className="text-[#C41E3A] font-bold text-base">{e.name_en}</h3>
                  <p className="text-sm text-[#C41E3A]/80 font-arabic">{e.name_ar}</p>
                </div>
                {!e.active && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-500/15 text-gray-400 border border-gray-500/30">INACTIVE</span>}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-gray-300 my-3">
                <div className="flex items-center gap-1.5"><Calendar size={11} className="text-gray-500" /> {formatDateTime(e.starts_at)}</div>
                <div className="flex items-center gap-1.5"><MapPin size={11} className="text-gray-500" /> {e.location}</div>
                <div className="flex items-center gap-1.5"><Users size={11} className="text-gray-500" /> {e.tickets_sold} / {e.capacity}</div>
                <div className="text-white font-semibold">{formatCurrency(e.ticket_price_fils)}</div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setEditing(e)}
                  className="flex-1 px-3 py-1.5 rounded text-xs bg-white/5 border border-white/10 text-white hover:bg-white/10">
                  Edit
                </button>
                <button onClick={() => setViewing(e)}
                  className="flex-1 px-3 py-1.5 rounded text-xs bg-white/5 border border-white/10 text-white hover:bg-white/10">
                  Bookings ({e.tickets_sold})
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EventFormModal
          event={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {viewing && <BookingsModal event={viewing} onClose={() => setViewing(null)} />}
      {redeemOpen && <DoorRedemptionModal onClose={() => setRedeemOpen(false)} />}
    </div>
  )
}

function EventFormModal({ event, onClose }: { event?: Event; onClose: () => void }) {
  const qc = useQueryClient()
  const [nameEn, setNameEn] = useState(event?.name_en ?? '')
  const [nameAr, setNameAr] = useState(event?.name_ar ?? '')
  const [descEn, setDescEn] = useState(event?.description_en ?? '')
  const [descAr, setDescAr] = useState(event?.description_ar ?? '')
  const [startsAt, setStartsAt] = useState(event?.starts_at?.slice(0, 16) ?? '')
  const [endsAt, setEndsAt]     = useState(event?.ends_at?.slice(0, 16) ?? '')
  const [location, setLocation] = useState(event?.location ?? '')
  const [capacity, setCapacity] = useState(event?.capacity?.toString() ?? '20')
  const [priceFils, setPriceFils] = useState(event?.ticket_price_fils?.toString() ?? '25000')
  const [maxPerUser, setMaxPerUser] = useState(event?.max_per_user?.toString() ?? '')
  const [imageKey, setImageKey] = useState(event?.image_key ?? '')
  const [active, setActive] = useState(event?.active !== 0)
  const [allowCash, setAllowCash] = useState(event?.allow_cash !== 0)
  const [allowCardOnArrival, setAllowCardOnArrival] = useState(event?.allow_card_on_arrival !== 0)
  const [allowCliq, setAllowCliq] = useState(event?.allow_cliq !== 0)
  const [allowCardOnline, setAllowCardOnline] = useState(event?.allow_card_online === 1)
  // Per-event notes-field config. Off by default; admin can opt in
  // and customise the placeholder shown to the customer on the
  // booking screen. Blank placeholders fall through to the app's
  // generic l10n default ("Any notes for your booking?").
  const [notesEnabled, setNotesEnabled] = useState(event?.notes_field_enabled === 1)
  const [notesPlaceholderEn, setNotesPlaceholderEn] = useState(event?.notes_field_placeholder_en ?? '')
  const [notesPlaceholderAr, setNotesPlaceholderAr] = useState(event?.notes_field_placeholder_ar ?? '')
  // Time-of-day handling per event:
  //   'none'    — no time concept (one-off events with starts_at carry their own time).
  //   'display' — show the opening-hours window on the booking screen, no customer pick.
  //   'pick'    — show the window AND let the customer pick an exact time within it.
  // Open/close are 'HH:MM' 24h strings; required when time_mode !== 'none'.
  const [timeMode, setTimeMode] = useState<'none' | 'display' | 'pick'>(event?.time_mode ?? 'none')
  const [dailyOpenTime, setDailyOpenTime] = useState(event?.daily_open_time ?? '')
  const [dailyCloseTime, setDailyCloseTime] = useState(event?.daily_close_time ?? '')
  // Whether the customer-facing booking screen + list card show a
  // "X seats left" indicator. On by default; admin opts out when the
  // venue doesn't want scarcity visible.
  const [showSeatsLeft, setShowSeatsLeft] = useState(event?.show_seats_left !== 0)
  // Refundable: when on AND payment was card, customer cancellation
  // triggers an automatic CyberSource refund. When off, card-paid
  // tickets can't be cancelled by the customer (cash / card-on-arrival
  // unaffected). Default on.
  const [refundable, setRefundable] = useState(event?.refundable !== 0)
  const [venueId, setVenueId] = useState<string | ''>(event?.venue_id ?? '')
  // Closed weekdays (ongoing experiences only) — 0=Sun..6=Sat. The
  // customer's date picker greys these out and the booking endpoint
  // rejects them. Parsed from the stored CSV.
  const [closedWeekdays, setClosedWeekdays] = useState<Set<number>>(() => {
    const csv = event?.closed_weekdays
    const s = new Set<number>()
    if (csv) for (const p of csv.split(',')) { const n = parseInt(p.trim(), 10); if (n >= 0 && n <= 6) s.add(n) }
    return s
  })

  const venuesQuery = useQuery<{ venues: { id: string; name_en: string; active: number }[] }>({
    queryKey: ['venues-active'],
    queryFn: () => api.get('/venues').then(r => r.data),
  })
  const venues = (venuesQuery.data?.venues ?? []).filter(v => v.active === 1)
  // Cancellation window in hours. Stored separately from refundable
  // so admin can offer "cancel any time" (open) or block cancels
  // entirely (0). Form represents:
  //   - 'preset' mode with one of {1, 24, 48, 168} hours (1h / 1d / 2d / 1w)
  //   - 'open' mode (no window — cancel any time before event)
  //   - 'never' mode (no cancellation at all)
  //   - 'custom' mode with a free-form hours input
  // Stored in DB as: null=open, 0=never, N>0=N hours.
  const initialCancelHours = event?.cancel_window_hours
  const [cancelMode, setCancelMode] = useState<'open' | 'never' | 'preset' | 'custom'>(() => {
    if (initialCancelHours === null || initialCancelHours === undefined) {
      // null = open. undefined happens only for "new" events; default 24h preset.
      return initialCancelHours === null ? 'open' : 'preset'
    }
    if (initialCancelHours === 0) return 'never'
    if ([1, 24, 48, 168].includes(initialCancelHours)) return 'preset'
    return 'custom'
  })
  const [cancelPresetHours, setCancelPresetHours] = useState<number>(() => {
    const h = initialCancelHours
    if (h !== null && h !== undefined && [1, 24, 48, 168].includes(h)) return h
    return 24
  })
  const [cancelCustomHours, setCancelCustomHours] = useState(() => {
    const h = initialCancelHours
    return h !== null && h !== undefined && ![0, 1, 24, 48, 168].includes(h) ? h.toString() : ''
  })
  // For ongoing experiences (no end date), the capacity is interpreted
  // as a DAILY limit by the server — we surface that distinction in
  // the form copy so the admin doesn't accidentally cap lifetime sales.
  const isOngoing = !endsAt
  // Multi-tier pricing. Each row is name/desc (EN+AR) + gross price
  // in fils. The first save creates new rows; subsequent saves
  // replace-all (worker-side). Empty list = single-price event using
  // ticket_price_fils, which keeps backward compat for events created
  // before migration 0085.
  const [tiers, setTiers] = useState<Array<{
    id?: string
    name_en: string
    name_ar: string
    description_en: string
    description_ar: string
    price_fils: string
  }>>(
    (event?.tiers ?? []).map(t => ({
      id: t.id,
      name_en: t.name_en,
      name_ar: t.name_ar,
      description_en: t.description_en ?? '',
      description_ar: t.description_ar ?? '',
      price_fils: t.price_fils.toString(),
    }))
  )
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      if (event) return api.patch(`/events/${event.id}`, payload).then(r => r.data)
      return api.post('/events', payload).then(r => r.data)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-events'] })
      onClose()
    },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/events/${event!.id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-events'] })
      onClose()
    },
  })

  async function handleUpload(file: File) {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/events/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setImageKey(res.data.key)
    } catch {
      setError('Image upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#2e2e2e] flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">{event ? 'Edit Event' : 'New Event'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Name (English)" value={nameEn} onChange={setNameEn} />
            <Input label="Name (Arabic)" value={nameAr} onChange={setNameAr} dir="rtl" />
            <Input label="Description (English)" value={descEn ?? ''} onChange={setDescEn} multiline />
            <Input label="Description (Arabic)" value={descAr ?? ''} onChange={setDescAr} multiline dir="rtl" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Location" value={location} onChange={setLocation} />
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Venue (optional)</label>
              <select
                value={venueId}
                onChange={e => setVenueId(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="">— No venue —</option>
                {venues.map(v => (
                  <option key={v.id} value={v.id}>{v.name_en}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">Routes booking notifications to the venue&apos;s manager via Notification routing. Leave blank to fall back to the topic default.</p>
            </div>
            <Input label="Starts at" value={startsAt} onChange={setStartsAt} type="datetime-local" />
            <Input label="Ends at (optional)" value={endsAt} onChange={setEndsAt} type="datetime-local" />
            <Input
              label={isOngoing ? 'Capacity per day' : 'Capacity'}
              value={capacity}
              onChange={setCapacity}
              type="number"
              hint={isOngoing
                ? 'Ongoing experience — counted per visit date, not lifetime.'
                : undefined}
            />
            <Input label="Ticket price (fils)" value={priceFils} onChange={setPriceFils} type="number"
              hint={parseInt(priceFils || '0') > 0 ? `JD ${(parseInt(priceFils) / 1000).toFixed(3)}` : undefined} />
            <Input label="Max per user (blank = unlimited)" value={maxPerUser} onChange={setMaxPerUser} type="number" />
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={active}
                  onChange={e => setActive(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={showSeatsLeft}
                  onChange={e => setShowSeatsLeft(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                <span>
                  Show tickets remaining to customers
                  <span className="block text-[10px] text-gray-500 font-normal">
                    Hides the "X seats left" badge on the booking screen when off.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className={labelCls}>Image</label>
            <p className="text-[11px] text-gray-500 mb-1.5">
              Recommended: <span className="text-gray-300">1280×720 px (16:9 landscape)</span>. Keep titles and logos centred — the edges may be trimmed slightly on some screens. The preview below shows the real 16:9 crop used in the app and on the site.
            </p>
            {imageKey && (
              <img src={`https://files.drinx.jo/${imageKey}`} alt=""
                className="w-full aspect-[16/9] object-cover rounded-lg mb-2 bg-[#0a0a0a]" />
            )}
            <input type="file" accept="image/*"
              onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])}
              disabled={uploading}
              className="text-xs text-gray-400 file:mr-2 file:px-3 file:py-1 file:rounded file:bg-white/10 file:text-white file:border-0 file:text-xs"
            />
            {uploading && <p className="text-xs text-gray-500 mt-1">Uploading…</p>}
          </div>

          <div>
            <label className={labelCls}>Payment methods customers can choose</label>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-1">
              <p className="col-span-1 text-[10px] uppercase tracking-wide text-gray-500 mt-1">Pay now</p>
              <p className="col-span-1 text-[10px] uppercase tracking-wide text-gray-500 mt-1">Pay on arrival</p>

              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={allowCardOnline}
                  onChange={e => setAllowCardOnline(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                Credit Card
              </label>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={allowCardOnArrival}
                  onChange={e => setAllowCardOnArrival(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                Card
              </label>

              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={allowCliq}
                  onChange={e => setAllowCliq(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                CliQ
              </label>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={allowCash}
                  onChange={e => setAllowCash(e.target.checked)}
                  className="w-4 h-4 accent-[#C41E3A]" />
                Cash
              </label>
            </div>
          </div>

          {/* Per-event notes-field config. Off by default; if on, the
              customer sees a single textarea on the booking screen with
              the configured placeholder. Use cases: food allergies,
              smoking preference, accessibility needs. */}
          <div className="border-t border-[#2e2e2e] pt-4">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" checked={notesEnabled}
                onChange={e => setNotesEnabled(e.target.checked)}
                className="w-4 h-4 accent-[#C41E3A]" />
              Show notes field on booking screen
            </label>
            {notesEnabled && (
              <div className="mt-3 grid grid-cols-2 gap-4">
                <Input
                  label="Notes placeholder (English)"
                  value={notesPlaceholderEn}
                  onChange={setNotesPlaceholderEn}
                  hint="e.g. Any food allergies or accessibility needs?"
                />
                <Input
                  label="Notes placeholder (Arabic)"
                  value={notesPlaceholderAr}
                  onChange={setNotesPlaceholderAr}
                  dir="rtl"
                />
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-1">
              Leave both blank to use the default "Any notes for your booking?".
            </p>
          </div>

          {/* Time-of-day handling. 'none' = no time concept (e.g. a
              one-off tasting whose starts_at carries the time). 'display'
              = show daily opening hours on the booking screen but no
              customer time pick. 'pick' = same window + customer picks
              an exact time within it (stored on the ticket). */}
          <div className="border-t border-[#2e2e2e] pt-4">
            <label className={labelCls}>Time of day</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {([
                { v: 'none',    label: 'No time',   sub: 'One-off event' },
                { v: 'display', label: 'Show hours', sub: 'Display only' },
                { v: 'pick',    label: 'Customer picks', sub: 'Within window' },
              ] as const).map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setTimeMode(opt.v)}
                  className={`text-left px-3 py-2 rounded-lg border transition ${
                    timeMode === opt.v
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                      : 'border-[#2e2e2e] bg-white/[0.02] text-gray-300 hover:bg-white/5'
                  }`}
                >
                  <div className="text-sm font-semibold">{opt.label}</div>
                  <div className="text-[10px] text-gray-500">{opt.sub}</div>
                </button>
              ))}
            </div>
            {timeMode !== 'none' && (
              <div className="mt-3 grid grid-cols-2 gap-4">
                <Input
                  label="Opens at"
                  value={dailyOpenTime}
                  onChange={setDailyOpenTime}
                  type="time"
                />
                <Input
                  label="Closes at"
                  value={dailyCloseTime}
                  onChange={setDailyCloseTime}
                  type="time"
                />
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-1">
              {timeMode === 'none' && 'Customers will not see any opening hours on the booking screen.'}
              {timeMode === 'display' && 'Customers will see the opening hours but cannot pick a specific time.'}
              {timeMode === 'pick' && 'Customers will pick a specific time within this window; the time is stored on their ticket.'}
            </p>
          </div>

          {/* Closed weekdays — ongoing experiences only. The customer's
              date picker greys these out; the booking endpoint rejects
              them as a backstop. One-off events have a fixed date, so
              this doesn't apply. */}
          {isOngoing && (
            <div className="border-t border-[#2e2e2e] pt-4">
              <label className={labelCls}>Closed on</label>
              <p className="text-[11px] text-gray-500 mb-2">Days this experience is unavailable to book. Customers can't pick these in the date picker.</p>
              <div className="flex flex-wrap gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((lbl, i) => {
                  const on = closedWeekdays.has(i)
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setClosedWeekdays(prev => {
                        const next = new Set(prev)
                        if (next.has(i)) next.delete(i); else next.add(i)
                        return next
                      })}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        on
                          ? 'bg-[#C41E3A]/15 border-[#C41E3A] text-[#C41E3A]'
                          : 'bg-[#1a1a1a] border-[#2e2e2e] text-gray-300 hover:bg-[#222]'
                      }`}
                    >
                      {lbl}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Refund + cancellation policy. Two controls:
              1. Refundable — drives whether card-paid cancellations
                 trigger an automatic CyberSource refund. Off = card
                 cancellations are blocked customer-side.
              2. Cancellation window — how long before the event the
                 customer can self-cancel. Independent from refundable
                 (cash/card-on-arrival cancels follow this window too,
                 they just don't need a refund). */}
          <div className="border-t border-[#2e2e2e] pt-4">
            <label className={labelCls}>Refund & cancellation policy</label>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer mt-1">
              <input type="checkbox" checked={refundable}
                onChange={e => setRefundable(e.target.checked)}
                className="w-4 h-4 accent-[#C41E3A]" />
              <span>
                Refund card payments automatically on cancellation
                <span className="block text-[10px] text-gray-500 font-normal">
                  When off, card-paid tickets can't be cancelled by the customer (admin can still force-cancel).
                </span>
              </span>
            </label>

            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Customer cancellation window</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {([
                  { mode: 'preset',  hours: 1,    label: '1 hour before' },
                  { mode: 'preset',  hours: 24,   label: '24 hours before' },
                  { mode: 'preset',  hours: 48,   label: '48 hours before' },
                  { mode: 'preset',  hours: 168,  label: '1 week before' },
                  { mode: 'open',    hours: null, label: 'Any time (open)' },
                  { mode: 'never',   hours: 0,    label: 'No cancellation' },
                  { mode: 'custom',  hours: null, label: 'Custom hours…' },
                ] as const).map((opt) => {
                  const selected =
                    (opt.mode === 'preset' && cancelMode === 'preset' && cancelPresetHours === opt.hours) ||
                    (opt.mode === 'open' && cancelMode === 'open') ||
                    (opt.mode === 'never' && cancelMode === 'never') ||
                    (opt.mode === 'custom' && cancelMode === 'custom')
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        setCancelMode(opt.mode)
                        if (opt.mode === 'preset' && opt.hours !== null) setCancelPresetHours(opt.hours)
                      }}
                      className={`text-left px-3 py-2 rounded-lg border text-xs font-semibold transition ${
                        selected
                          ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                          : 'border-[#2e2e2e] bg-white/[0.02] text-gray-300 hover:bg-white/5'
                      }`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
              {cancelMode === 'custom' && (
                <div className="mt-2">
                  <Input
                    label="Hours before event"
                    value={cancelCustomHours}
                    onChange={setCancelCustomHours}
                    type="number"
                    hint="Customer can cancel up to N hours before event start."
                  />
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-2">
                {cancelMode === 'open' && 'Customers can cancel at any time before the event begins.'}
                {cancelMode === 'never' && 'Customers cannot self-cancel. Admin can still cancel from this page.'}
                {cancelMode === 'preset' && `Customers can cancel up to ${cancelPresetHours} ${cancelPresetHours === 1 ? 'hour' : 'hours'} before the event.`}
                {cancelMode === 'custom' && cancelCustomHours && `Customers can cancel up to ${cancelCustomHours} hours before the event.`}
              </p>
            </div>
          </div>

          {/* Pricing tiers — Vineyard Tour shape: Classic / Reserve /
              Limited / Signature, each with its own gross-fils price.
              When empty, the event uses the single ticket_price_fils
              above. When non-empty, customer picks a tier at booking
              and that tier's price drives the ticket total. */}
          <div className="border-t border-[#2e2e2e] pt-4">
            <label className={`${labelCls} mb-2 block`}>
              Pricing tiers <span className="text-gray-500 font-normal text-[11px]">(optional — leave empty for single-price)</span>
            </label>
            {tiers.length === 0 && (
              <p className="text-[11px] text-gray-500 mb-2">No tiers — customers will pay <span className="text-gray-300">JD {(parseInt(priceFils || '0') / 1000).toFixed(3)}</span> per ticket.</p>
            )}
            {tiers.map((tier, idx) => (
              <div key={idx} className="rounded-lg border border-[#2e2e2e] bg-white/[0.02] p-3 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">Tier {idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => setTiers(tiers.filter((_, i) => i !== idx))}
                    className="text-xs text-red-400 hover:text-red-300"
                  >Remove</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Name (English)"
                    value={tier.name_en}
                    onChange={(v) => setTiers(tiers.map((t, i) => i === idx ? { ...t, name_en: v } : t))}
                  />
                  <Input
                    label="Name (Arabic)"
                    value={tier.name_ar}
                    onChange={(v) => setTiers(tiers.map((t, i) => i === idx ? { ...t, name_ar: v } : t))}
                    dir="rtl"
                  />
                  <Input
                    label="Description (English, optional)"
                    value={tier.description_en}
                    onChange={(v) => setTiers(tiers.map((t, i) => i === idx ? { ...t, description_en: v } : t))}
                  />
                  <Input
                    label="Description (Arabic, optional)"
                    value={tier.description_ar}
                    onChange={(v) => setTiers(tiers.map((t, i) => i === idx ? { ...t, description_ar: v } : t))}
                    dir="rtl"
                  />
                  <Input
                    label="Price (fils, gross / ++)"
                    value={tier.price_fils}
                    onChange={(v) => setTiers(tiers.map((t, i) => i === idx ? { ...t, price_fils: v } : t))}
                    type="number"
                    hint={parseInt(tier.price_fils || '0') > 0
                      ? `JD ${(parseInt(tier.price_fils) / 1000).toFixed(3)}`
                      : undefined}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setTiers([...tiers, {
                name_en: '', name_ar: '',
                description_en: '', description_ar: '',
                price_fils: '',
              }])}
              className="w-full text-xs px-3 py-2 rounded-lg bg-white/5 border border-dashed border-white/15 text-gray-300 hover:bg-white/10 hover:text-white"
            >
              + Add tier
            </button>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => save.mutate({
                name_en: nameEn, name_ar: nameAr,
                description_en: descEn || null, description_ar: descAr || null,
                location,
                starts_at: startsAt ? new Date(startsAt).toISOString() : '',
                ends_at: endsAt ? new Date(endsAt).toISOString() : null,
                capacity: parseInt(capacity || '0', 10),
                ticket_price_fils: parseInt(priceFils || '0', 10),
                max_per_user: maxPerUser ? parseInt(maxPerUser, 10) : null,
                image_key: imageKey || null,
                active,
                allow_cash: allowCash,
                allow_card_on_arrival: allowCardOnArrival,
                allow_cliq: allowCliq,
                allow_card_online: allowCardOnline,
                notes_field_enabled: notesEnabled,
                notes_field_placeholder_en: notesPlaceholderEn.trim() || null,
                notes_field_placeholder_ar: notesPlaceholderAr.trim() || null,
                time_mode: timeMode,
                daily_open_time: timeMode === 'none' ? null : (dailyOpenTime || null),
                daily_close_time: timeMode === 'none' ? null : (dailyCloseTime || null),
                show_seats_left: showSeatsLeft,
                refundable,
                cancel_window_hours:
                  cancelMode === 'open' ? null
                  : cancelMode === 'never' ? 0
                  : cancelMode === 'preset' ? cancelPresetHours
                  : (parseInt(cancelCustomHours || '24', 10) || 24),
                venue_id: venueId || null,
                // Only meaningful for ongoing experiences; send [] for
                // one-off events so a stale value doesn't linger.
                closed_weekdays: isOngoing ? [...closedWeekdays] : [],
                tiers: tiers.map((t, idx) => ({
                  id: t.id,
                  name_en: t.name_en.trim(),
                  name_ar: t.name_ar.trim(),
                  description_en: t.description_en.trim() || null,
                  description_ar: t.description_ar.trim() || null,
                  price_fils: parseInt(t.price_fils || '0', 10),
                  sort_order: idx,
                })),
              })}
              disabled={save.isPending}
              className="flex-1 px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : event ? 'Save' : 'Create'}
            </button>
            {event && (
              <button onClick={() => remove.mutate()}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-red-400 text-sm hover:bg-white/10">
                Deactivate
              </button>
            )}
            <button onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BookingsModal({ event, onClose }: { event: Event; onClose: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{ tickets: Ticket[] }>({
    queryKey: ['admin-event-tickets', event.id],
    queryFn: () => api.get(`/events/${event.id}/tickets`).then(r => r.data),
  })
  // Ticket pending cancel + the admin's refund choice for it.
  const [cancelling, setCancelling] = useState<Ticket | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const cancelMutation = useMutation({
    mutationFn: (args: { ticketId: string; refund: boolean }) =>
      api.post(`/events/tickets/${args.ticketId}/cancel`, { refund: args.refund }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-event-tickets', event.id] })
      qc.invalidateQueries({ queryKey: ['admin-events'] })
      setCancelling(null)
      setCancelError(null)
    },
    onError: (e: any) => setCancelError(e.response?.data?.message ?? 'Cancellation failed'),
  })

  const tickets = data?.tickets ?? []
  const totals = tickets.reduce((acc, t) => {
    if (t.status === 'cancelled') return acc
    acc.attendees += t.quantity
    acc.revenue   += t.total_paid_fils
    if (t.status === 'used') acc.checkedIn += t.quantity
    return acc
  }, { attendees: 0, revenue: 0, checkedIn: 0 })

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#2e2e2e] flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate">{event.name_en}</h2>
            <p className="text-xs text-gray-500">{formatDateTime(event.starts_at)} · {event.location}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <CsvControls
              basePath={`/events/${event.id}/tickets`}
              exportFilename={`bookings-${event.name_en.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`}
              exportOnly
            />
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 p-5 border-b border-[#2e2e2e]">
          <Stat label="Attendees" value={`${totals.attendees} / ${event.capacity}`} />
          <Stat label="Checked in" value={`${totals.checkedIn}`} />
          <Stat label="Revenue" value={formatCurrency(totals.revenue)} />
        </div>

        <div className="p-5">
          {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {!isLoading && tickets.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-6">No bookings yet.</p>
          )}
          <div className="space-y-2">
            {tickets.map(t => (
              <div key={t.id} className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{t.user_name}</p>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} /> {t.user_phone}</p>
                  {(t.attendance_date || t.attendance_time) && (
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Visiting: {t.attendance_date}{t.attendance_time ? ` · ${t.attendance_time}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-300">{t.quantity} × tickets</p>
                  <p className="text-xs text-gray-500">
                    {formatCurrency(t.total_paid_fils)} · {t.payment_method}
                    {t.card_brand && t.card_last4 ? ` (${t.card_brand} ${t.card_last4})` : ''}
                  </p>
                  {/* Refund state for cancelled card-paid tickets. Three
                      cases: refunded via DrinX, cancelled out-of-band,
                      or non-card cancellation (no money to return). */}
                  {t.status === 'cancelled' && t.payment_method === 'card' && t.cs_transaction_id && (
                    <p className={`text-[10px] mt-0.5 ${t.refund_transaction_id ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {t.refund_transaction_id
                        ? `Refunded ${formatCurrency(t.total_paid_fils)}`
                        : 'No refund issued · settled out-of-band'}
                    </p>
                  )}
                </div>
                {/* Redemption code hidden on cancelled rows so door staff
                    can't accidentally scan a no-longer-valid code. Still
                    stored server-side for audit / refund references. */}
                {t.status === 'cancelled' ? (
                  <div className="font-mono text-xs text-gray-500 bg-white/[0.02] border border-[#2e2e2e] px-2 py-1 rounded">
                    —
                  </div>
                ) : (
                  <div className="font-mono text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded">
                    {t.redemption_code}
                  </div>
                )}
                {t.status === 'confirmed' && (
                  <button
                    onClick={() => { setCancelError(null); setCancelling(t) }}
                    className="text-[11px] px-2 py-1 rounded border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold"
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {cancelling && (
        <CancelTicketConfirm
          ticket={cancelling}
          isPending={cancelMutation.isPending}
          error={cancelError}
          onConfirm={(refund) => cancelMutation.mutate({ ticketId: cancelling.id, refund })}
          onClose={() => { setCancelling(null); setCancelError(null) }}
        />
      )}
    </div>
  )
}

function CancelTicketConfirm({
  ticket, isPending, error, onConfirm, onClose,
}: {
  ticket: Ticket
  isPending: boolean
  error: string | null
  onConfirm: (refund: boolean) => void
  onClose: () => void
}) {
  // Default the refund checkbox: ON for card-paid with a transaction
  // id (CyberSource can actually refund), OFF for cash / card-on-arrival
  // / cliq (nothing to refund). Admin can still toggle either way.
  const refundable = ticket.payment_method === 'card' && ticket.cs_transaction_id !== null
  const [refund, setRefund] = useState(refundable)
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-md p-5">
        <h3 className="text-white font-bold mb-1">Cancel ticket?</h3>
        <p className="text-sm text-gray-400 mb-3">
          {ticket.user_name} · {ticket.quantity} × {formatCurrency(ticket.total_paid_fils)}
          {ticket.payment_method === 'card' && ticket.card_brand && ticket.card_last4
            ? ` · paid by ${ticket.card_brand} •••• ${ticket.card_last4}`
            : ` · paid via ${ticket.payment_method}`}
        </p>

        {ticket.payment_method === 'card' && !ticket.cs_transaction_id && (
          <p className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mb-3">
            This card-paid ticket has no CyberSource transaction id (likely booked before card support shipped). Auto-refund unavailable — handle out-of-band.
          </p>
        )}

        {refundable && (
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={refund}
              onChange={e => setRefund(e.target.checked)}
              className="w-4 h-4 accent-[#C41E3A]"
            />
            <span>
              Refund {formatCurrency(ticket.total_paid_fils)} to the customer's card now
              <span className="block text-[10px] text-gray-500 font-normal">
                Uncheck if reimbursing out-of-band (cash, bank transfer, etc.)
              </span>
            </span>
          </label>
        )}

        {error && <p className="text-red-400 text-xs mb-2">{error}</p>}

        <div className="flex gap-2">
          <button
            onClick={() => onConfirm(refund)}
            disabled={isPending}
            className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Cancelling…' : refund && refundable ? 'Cancel + refund' : 'Cancel ticket'}
          </button>
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-sm hover:bg-white/10 disabled:opacity-50"
          >
            Keep
          </button>
        </div>
      </div>
    </div>
  )
}

function DoorRedemptionModal({ onClose }: { onClose: () => void }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  const redeem = useMutation({
    mutationFn: () => api.post('/events/redeem', { phone, code }).then(r => r.data),
    onSuccess: (data) => {
      setSuccess(`Checked in: ${data.ticket.quantity} × ${data.ticket.event_name}`)
      setError(null)
      setPhone(''); setCode('')
      qc.invalidateQueries({ queryKey: ['admin-event-tickets'] })
      qc.invalidateQueries({ queryKey: ['admin-events'] })
    },
    onError: (e: any) => {
      setError(e.response?.data?.message ?? 'Could not check in')
      setSuccess(null)
    },
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-md">
        <div className="p-5 border-b border-[#2e2e2e] flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <KeyRound size={18} className="text-[#C41E3A]" /> Door Check-in
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-400">
            Enter the customer's phone and the 6-digit code shown on their ticket.
          </p>
          <Input label="Customer phone" value={phone} onChange={setPhone} />
          <Input label="6-digit code" value={code} onChange={setCode} type="number" />

          {success && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-emerald-400 mt-0.5" />
              <p className="text-sm text-emerald-300">{success}</p>
            </div>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}

          <button
            onClick={() => redeem.mutate()}
            disabled={!phone || !code || redeem.isPending}
            className="w-full px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
          >
            {redeem.isPending ? 'Checking…' : 'Check In'}
          </button>
        </div>
      </div>
    </div>
  )
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#C41E3A]'
const labelCls = 'block text-xs text-gray-400 mb-1'

function Input({ label, value, onChange, type = 'text', multiline = false, dir, hint }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; multiline?: boolean; dir?: 'rtl' | 'ltr'; hint?: string
}) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} dir={dir} rows={2} className={inputCls} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} dir={dir} className={inputCls} />
      )}
      {hint && <p className="text-xs text-gray-600 mt-1">{hint}</p>}
    </div>
  )
}

function StatusBadge({ status }: { status: Ticket['status'] }) {
  const cls = status === 'used'
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : status === 'cancelled'
      ? 'bg-gray-500/15 text-gray-400 border-gray-500/30'
      : 'bg-blue-500/15 text-blue-400 border-blue-500/30'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${cls}`}>{status}</span>
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  )
}
